/**
 * FilterDropdown — Multi-select filter with count badge.
 *
 * Reusable for Status, Type, and Organization filters.
 * Shows "Label (N)" on the trigger when N items are selected.
 * Uses existing Dropdown shell for popover positioning/animation
 * and CheckboxBase for visual selection indication.
 * Trigger shows brand colors when items are selected.
 */
import { ChevronDown } from "@untitledui/icons";
import { Button as AriaButton, Menu as AriaMenu, MenuItem as AriaMenuItem, type Selection } from "react-aria-components";
import { CheckboxBase } from "@/components/base/checkbox/checkbox";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { cx } from "@/utils/cx";

export interface FilterOption {
    id: string;
    label: string;
}

interface FilterDropdownProps {
    /** Label shown on the trigger button (e.g. "Status", "Type") */
    label: string;
    /** Available filter options */
    options: FilterOption[];
    /** Currently selected option IDs */
    selectedKeys: Set<string>;
    /** Called when selection changes */
    onSelectionChange: (keys: Set<string>) => void;
}

export function FilterDropdown({ label, options, selectedKeys, onSelectionChange }: FilterDropdownProps) {
    const count = selectedKeys.size;
    const hasSelection = count > 0;

    const handleSelectionChange = (selection: Selection) => {
        if (selection === "all") {
            onSelectionChange(new Set(options.map((o) => o.id)));
        } else {
            onSelectionChange(new Set(selection as Set<string>));
        }
    };

    return (
        <Dropdown.Root>
            <AriaButton
                className={cx(
                    "inline-flex cursor-pointer items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium outline-hidden transition duration-100 ease-linear",
                    hasSelection
                        ? "border-brand bg-brand-primary text-brand-secondary"
                        : "border-secondary bg-primary text-quaternary hover:border-primary hover:text-tertiary",
                )}
            >
                <span className="max-w-[64px] truncate">{hasSelection ? `${label} (${count})` : label}</span>
                <ChevronDown className="size-3 transition-transform duration-200" />
            </AriaButton>
            <Dropdown.Popover>
                <AriaMenu
                    selectionMode="multiple"
                    selectedKeys={selectedKeys}
                    onSelectionChange={handleSelectionChange}
                    className="h-min overflow-y-auto py-1 outline-hidden select-none"
                >
                    {options.map((opt) => (
                        <AriaMenuItem key={opt.id} id={opt.id} textValue={opt.label} className="group block cursor-pointer px-1.5 py-px outline-hidden">
                            {({ isSelected, isFocused, isFocusVisible }) => (
                                <div
                                    className={cx(
                                        "relative flex items-center gap-2 rounded-md px-2.5 py-2 outline-focus-ring transition duration-100 ease-linear",
                                        "group-hover:bg-primary_hover",
                                        isFocused && "bg-primary_hover",
                                        isFocusVisible && "outline-2 -outline-offset-2",
                                    )}
                                >
                                    <CheckboxBase size="sm" isSelected={isSelected} />
                                    <span className="grow truncate text-sm font-semibold text-secondary">{opt.label}</span>
                                </div>
                            )}
                        </AriaMenuItem>
                    ))}
                </AriaMenu>
            </Dropdown.Popover>
        </Dropdown.Root>
    );
}
